import {
  OrganizationMemberOrdering,
  Shared__enums__enums__SortOrder as SortOrder,
  TemplateSortBy,
  type TemplateType,
  type TemplateVisibility,
} from '@heidi/schema/ml-scribe';
import { atomWithHash } from '@heidi/utils/jotai-helpers';
import { type OrganizationMembersView } from '@heidi/workspace/contracts';
import { atom } from 'jotai';

export const isBulkInviteModalOpenAtom = atom<boolean>(false);

export const isOrganizationSettingsModalOpenAtom = atom<boolean>(false);

export const isOrgUserRolesInfoModalOpenAtom = atom<boolean>(false);

export const selectedOrgMemberAtom = atom<OrganizationMembersView | null>(null);

export const isChangeUserRoleModalOpenAtom = atom<boolean>(false);

export const ALL_FILTER_VALUE = 'ALL';

export interface OrganizationMembersFilters {
  searchTerm: string;
  teamFilter: string;
  roleFilter: string;
  specialtyFilter: string;
  showPendingInvitations: boolean;
  currentPage: number;
  sortField: OrganizationMemberOrdering;
  pageSize: number;
  isDescending: boolean;
}

export interface OrganizationTemplatesFilters {
  keyword: string;
  page: number;
  pageSize: number;
  sortBy: TemplateSortBy;
  order: SortOrder;
  types: TemplateType | typeof ALL_FILTER_VALUE;
  creatorIds: string;
  visibilities: TemplateVisibility[] | typeof ALL_FILTER_VALUE;
  sharedTeamIds: string[];
}

export const defaultMembersFilters: OrganizationMembersFilters = {
  searchTerm: '',
  teamFilter: ALL_FILTER_VALUE,
  roleFilter: ALL_FILTER_VALUE,
  specialtyFilter: ALL_FILTER_VALUE,
  showPendingInvitations: false,
  currentPage: 1,
  sortField: OrganizationMemberOrdering.email,
  pageSize: 10,
  isDescending: false,
};

export const defaultTemplatesFilters: OrganizationTemplatesFilters = {
  keyword: '',
  page: 1,
  pageSize: 10,
  sortBy: TemplateSortBy.NAME,
  order: SortOrder.ASC,
  types: ALL_FILTER_VALUE,
  creatorIds: ALL_FILTER_VALUE,
  visibilities: ALL_FILTER_VALUE,
  sharedTeamIds: [],
};

export const organizationMembersFiltersAtom = atomWithHash<OrganizationMembersFilters>(
  'orgMembersFilters',
  defaultMembersFilters
);

export const organizationTemplatesFiltersAtom =
  atomWithHash<OrganizationTemplatesFilters>(
    'orgTemplatesFilters',
    defaultTemplatesFilters
  );

/**
 * Filter state for the redesigned (`DataTable`-based) organisation members
 * table. Separate from {@link OrganizationMembersFilters} because the redesign
 * moves team / role / specialty from a single scalar `ALL`-sentinel value to
 * multi-select id arrays — widening the legacy shape in place would change what
 * the still-live bespoke table reads.
 */
export interface OrganizationMembersTableFilters {
  searchTerm: string;
  teamIds: string[];
  roleIds: string[];
  specialtyIds: string[];
  showPendingInvitations: boolean;
  currentPage: number;
  pageSize: number;
  sortField: OrganizationMemberOrdering;
  isDescending: boolean;
}

export const defaultMembersTableFilters: OrganizationMembersTableFilters = {
  searchTerm: '',
  teamIds: [],
  roleIds: [],
  specialtyIds: [],
  showPendingInvitations: false,
  currentPage: 1,
  pageSize: 10,
  sortField: OrganizationMemberOrdering.email,
  isDescending: false,
};

// Distinct hash key from `orgMembersFilters` so the two tables can't desync over
// one payload shape while both are reachable. Renamed to the canonical key when
// the legacy table is deleted.
export const organizationMembersTableFiltersAtom =
  atomWithHash<OrganizationMembersTableFilters>(
    'orgMembersTable',
    defaultMembersTableFilters,
    {
      // `atomWithHash`'s default deserialize returns the parsed payload verbatim,
      // so a link written by an older build would resolve missing keys to
      // `undefined` while the type says they're required. Merge over the defaults.
      deserialize: (value) => {
        try {
          return {
            ...defaultMembersTableFilters,
            ...(JSON.parse(value) as Partial<OrganizationMembersTableFilters>),
          };
        } catch {
          return defaultMembersTableFilters;
        }
      },
    }
  );

export enum MemberRemovalReason {
  GLOBAL_ADMIN = 'global_admin',
  TEAM_OWNER = 'team_owner',
}
